import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, GripVertical, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { JOB_LABEL, TERRAIN_LABEL, LOAD_LABEL, DURATION_LABEL } from "../../lib/matSelector";

const MATERIALS = ["Timber", "Composite", "Steel / Aluminum", "Rubber"];
const LOAD_RATINGS = ["Light", "Medium", "Heavy"];
const FUNCTIONS = [
  { v: "access", l: "Access / Roadway" }, { v: "crane-pad", l: "Crane / Outrigger" },
  { v: "turf", l: "Turf / Ground Cover" }, { v: "rig", l: "Rig Platform" }, { v: "trench", l: "Trench / Excavation" },
];
const toOpts = (map) => Object.entries(map).map(([v, l]) => ({ v, l }));
const JOB_OPTS = toOpts(JOB_LABEL);
const TERRAIN_OPTS = toOpts(TERRAIN_LABEL);
const LOAD_OPTS = toOpts(LOAD_LABEL);
const DURATION_OPTS = toOpts(DURATION_LABEL);

const EMPTY = {
  name: "", material: "Composite", functions: [], load_rating: "Medium", dimensions: "",
  weight_capacity: "", applications: [], description: "", image: "", rental_price: "",
  purchase_price: "", published: true, order: 100, job_types: [], terrains: [], loads: [],
  durations: [], env_sensitive: false, rent_friendly: true,
};

export default function MatManager() {
  const [mats, setMats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // mat object or "new"

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/mats").then(({ data }) => setMats(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? This removes it from the catalog and selector.`)) return;
    try {
      await api.delete(`/admin/mats/${m.id}`);
      toast.success("Mat removed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="mat-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-xl text-white">Mat Catalog</h2>
          <p className="text-sm text-white/50 mt-1">Manage the mats shown in the catalog & selector. Prices stay internal (never public).</p>
        </div>
        <button data-testid="add-mat-btn" onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2.5 text-sm font-semibold hover:bg-flame/90 transition-colors">
          <Plus className="h-4 w-4" /> Add mat
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-white/50"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid gap-3">
          {mats.map((m) => (
            <div key={m.id} data-testid={`admin-mat-${m.id}`} className="flex items-center gap-4 border border-white/10 bg-white/[0.02] p-3">
              <div className="h-14 w-20 bg-ink shrink-0 overflow-hidden">
                {m.image && <img src={m.image} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-white truncate">{m.name}</span>
                  {m.published === false && <span className="inline-flex items-center gap-1 text-[10px] uppercase text-white/40"><EyeOff className="h-3 w-3" /> hidden</span>}
                </div>
                <div className="text-xs text-white/50 mt-0.5">{m.material} · {m.load_rating} duty · {(m.functions || []).length} functions</div>
              </div>
              <button data-testid={`edit-mat-${m.id}`} onClick={() => setEditing(m)} className="p-2 text-white/60 hover:text-flame transition-colors"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-mat-${m.id}`} onClick={() => remove(m)} className="p-2 text-white/60 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <MatEditor mat={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function MatEditor({ mat, onClose, onSaved }) {
  const [form, setForm] = useState(mat ? {
    ...EMPTY, ...mat,
    applications: mat.applications || [], functions: mat.functions || [],
    job_types: mat.job_types || [], terrains: mat.terrains || [], loads: mat.loads || [], durations: mat.durations || [],
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      order: Number(form.order) || 100,
      applications: Array.isArray(form.applications) ? form.applications : String(form.applications).split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (mat) await api.put(`/admin/mats/${mat.id}`, payload);
      else await api.post("/admin/mats", payload);
      toast.success(mat ? "Mat updated" : "Mat added");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="mat-editor" onClick={onClose}>
      <div className="bg-ink border border-white/15 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-ink z-10">
          <h3 className="font-display font-bold text-lg text-white">{mat ? "Edit mat" : "Add mat"}</h3>
          <button onClick={onClose} data-testid="close-editor" className="p-1 text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={save} className="p-5 space-y-5">
          <TextField label="Name *" testid="mat-name" value={form.name} onChange={set("name")} />
          <div className="grid sm:grid-cols-3 gap-4">
            <SelectField label="Material" testid="mat-material" value={form.material} onChange={set("material")} options={MATERIALS} />
            <SelectField label="Load rating" testid="mat-load-rating" value={form.load_rating} onChange={set("load_rating")} options={LOAD_RATINGS} />
            <TextField label="Sort order" testid="mat-order" type="number" value={form.order} onChange={set("order")} />
          </div>

          <div>
            <Label>Functions</Label>
            <div className="flex flex-wrap gap-2">
              {FUNCTIONS.map((f) => (
                <TagBtn key={f.v} active={form.functions.includes(f.v)} testid={`fn-${f.v}`} onClick={() => toggleArr("functions", f.v)}>{f.l}</TagBtn>
              ))}
            </div>
          </div>

          <TextField label="Dimensions" testid="mat-dimensions" value={form.dimensions} onChange={set("dimensions")} />
          <TextField label="Weight capacity (PSI / GVW)" testid="mat-capacity" value={form.weight_capacity} onChange={set("weight_capacity")} />
          <TextArea label="Description" testid="mat-description" rows={3} value={form.description} onChange={set("description")} />
          <TextArea label="Typical applications (one per line)" testid="mat-applications" rows={4}
            value={Array.isArray(form.applications) ? form.applications.join("\n") : form.applications}
            onChange={(e) => setForm({ ...form, applications: e.target.value.split("\n") })} />
          <TextField label="Image URL" testid="mat-image" value={form.image} onChange={set("image")} placeholder="https://..." />
          {form.image && <img src={form.image} alt="" className="h-28 w-auto border border-white/10 object-cover" />}

          <div className="grid sm:grid-cols-2 gap-4 border-t border-white/10 pt-5">
            <TextField label="Rental price (internal)" testid="mat-rental" value={form.rental_price} onChange={set("rental_price")} placeholder="Not shown publicly" />
            <TextField label="Purchase price (internal)" testid="mat-purchase" value={form.purchase_price} onChange={set("purchase_price")} placeholder="Not shown publicly" />
          </div>

          <div className="flex flex-wrap gap-6">
            <Check label="Published (visible on site)" testid="mat-published" checked={form.published} onChange={(v) => setForm({ ...form, published: v })} />
            <Check label="Environmentally safe (non-toxic)" testid="mat-env" checked={form.env_sensitive} onChange={(v) => setForm({ ...form, env_sensitive: v })} />
            <Check label="Rental-friendly" testid="mat-rentfriendly" checked={form.rent_friendly} onChange={(v) => setForm({ ...form, rent_friendly: v })} />
          </div>

          {/* Advanced selector matching */}
          <div className="border-t border-white/10 pt-4">
            <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-sm font-semibold text-flame">
              {showAdvanced ? "− Hide" : "+ Show"} selector matching (advanced)
            </button>
            {showAdvanced && (
              <div className="mt-4 space-y-4">
                <p className="text-xs text-white/40">These tags control which answers this mat matches in the Mat Selector tool.</p>
                <TagGroup label="Job types" opts={JOB_OPTS} selected={form.job_types} onToggle={(v) => toggleArr("job_types", v)} prefix="job" />
                <TagGroup label="Terrains" opts={TERRAIN_OPTS} selected={form.terrains} onToggle={(v) => toggleArr("terrains", v)} prefix="terrain" />
                <TagGroup label="Suitable loads" opts={LOAD_OPTS} selected={form.loads} onToggle={(v) => toggleArr("loads", v)} prefix="load" />
                <TagGroup label="Durations" opts={DURATION_OPTS} selected={form.durations} onToggle={(v) => toggleArr("durations", v)} prefix="dur" />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-white/10 pt-5">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-white/70 hover:text-white">Cancel</button>
            <button type="submit" data-testid="save-mat" disabled={saving}
              className="inline-flex items-center gap-2 bg-flame text-white px-6 py-2.5 text-sm font-semibold hover:bg-flame/90 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {mat ? "Save changes" : "Add mat"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Label = ({ children }) => <label className="block text-xs uppercase tracking-wide text-white/50 mb-2">{children}</label>;

function TextField({ label, testid, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full bg-white/5 border border-white/15 px-3.5 py-2.5 text-white placeholder:text-white/25 text-sm focus:outline-none focus:border-flame" />
    </div>
  );
}
function TextArea({ label, testid, value, onChange, rows }) {
  return (
    <div>
      <Label>{label}</Label>
      <textarea data-testid={testid} rows={rows} value={value} onChange={onChange}
        className="w-full bg-white/5 border border-white/15 px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-flame resize-none" />
    </div>
  );
}
function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <Label>{label}</Label>
      <select data-testid={testid} value={value} onChange={onChange}
        className="w-full bg-white/5 border border-white/15 px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-flame">
        {options.map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
      </select>
    </div>
  );
}
function TagBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`px-3 py-1.5 text-xs border transition-colors ${active ? "bg-flame text-white border-flame" : "bg-white/5 text-white/70 border-white/15 hover:border-flame"}`}>
      {children}
    </button>
  );
}
function TagGroup({ label, opts, selected, onToggle, prefix }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => (
          <TagBtn key={o.v} active={selected.includes(o.v)} testid={`${prefix}-${o.v}`} onClick={() => onToggle(o.v)}>{o.l}</TagBtn>
        ))}
      </div>
    </div>
  );
}
function Check({ label, testid, checked, onChange }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid={testid} onClick={() => onChange(!checked)}>
      <span className={`h-5 w-5 border flex items-center justify-center ${checked ? "bg-flame border-flame" : "border-white/30"}`}>
        {checked && <span className="h-2 w-2 bg-white" />}
      </span>
      <span className="text-sm text-white/80">{label}</span>
    </label>
  );
}
